import { StyleType, wixFont, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  StyleParamType,
  createStylesParams,
  createStylesParam,
  wixFontParam,
  wixColorParam,
} from '@wix/tpa-settings';

export type IStylesParams = {
  isBlogComments: StyleParamType.Boolean;
  commentsSpacing: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  replyLeftPadding: StyleParamType.Number;
  commentCountFont: StyleParamType.Font;
  commentCountMobileFontSize: StyleParamType.Number;
  commentCountColor: StyleParamType.Color;
  sortingFont: StyleParamType.Font;
  sortingMobileFontSize: StyleParamType.Number;
  sortingColor: StyleParamType.Color;
  headerDividerColor: StyleParamType.Color;
  headerDividerWidth: StyleParamType.Number;
  commentBoxPlaceholderColor: StyleParamType.Color;
  commentBoxPlaceholderMobileFontSize: StyleParamType.Number;
  commentBoxPlaceholderFont: StyleParamType.Font;
  closedCommentBoxFillColor: StyleParamType.Color;
  closedCommentBoxBorderColor: StyleParamType.Color;
  closedCommentBoxBorderWidth: StyleParamType.Number;
  closedCommentBoxCornerRadius: StyleParamType.Number;
  openCommentBoxTextColor: StyleParamType.Color;
  pluginsIconColor: StyleParamType.Color;
  openCommentBoxFillColor: StyleParamType.Color;
  openCommentBoxBorderColor: StyleParamType.Color;
  buttonColor: StyleParamType.Color;
  buttonFont: StyleParamType.Font;
  buttonTextColor: StyleParamType.Color;
  buttonCornerRadius: StyleParamType.Number;
  commentListCommentFont: StyleParamType.Font;
  commentListCommentMobileFontSize: StyleParamType.Number;
  commentListCommentTextColor: StyleParamType.Color;
  commentListSecondaryFont: StyleParamType.Font;
  commentListSecondaryMobileFontSize: StyleParamType.Number;
  commentListSecondaryTextColor: StyleParamType.Color;
  commentListUsernameFont: StyleParamType.Font;
  commentListUsernameMobileFontSize: StyleParamType.Number;
  commentListUsernameTextColor: StyleParamType.Color;
  commentListBadgeColor: StyleParamType.Color;
  commentListHighlightColor: StyleParamType.Color;
  markedCommentCornerRadius: StyleParamType.Number;
  actionsFont: StyleParamType.Font;
  actionsTextColor: StyleParamType.Color;
  actionsIconColor: StyleParamType.Color;
  commentListDividerColor: StyleParamType.Color;
  widgetBackgroundColor: StyleParamType.Color;
  widgetBorderColor: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetCornerRadius: StyleParamType.Number;
  filledRatingStar: StyleParamType.Color;
  emptyRatingStar: StyleParamType.Color;
  ratingInfoFont: StyleParamType.Font;
  ratingInfoColor: StyleParamType.Color;
  headerButtonFont: StyleParamType.Font;
  headerButtonColor: StyleParamType.Color;
  headerButtonRadius: StyleParamType.Number;
};

const isBlogComments = createStylesParam('isBlogComments', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

export default createStylesParams<IStylesParams>({
  isBlogComments,
  commentCountFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 20 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-heading-2-font'),
      ai: {
        description:
          'Font style for the comment count heading displayed in the widget header (e.g. "3 Comments").',
      },
    },
  },
  commentCountMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
      ai: {
        description:
          'Font size for the comment count heading on mobile devices, in pixels. Range: 12-20.',
      },
    },
  },
  commentCountColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Text color for the comment count heading displayed in the widget header.',
      },
    },
  },
  sortingFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description:
          'Font style for the comment sorting control (e.g. "Newest", "Oldest") in the widget header.',
      },
    },
  },
  sortingMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
      ai: {
        description:
          'Font size for the comment sorting control on mobile devices, in pixels. Range: 12-16.',
      },
    },
  },
  sortingColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Text color for the comment sorting control in the widget header.',
      },
    },
  },
  headerButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description: 'Font style for header action buttons (e.g. "Subscribe", "Share").',
      },
    },
  },
  headerButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
      ai: {
        description: 'Text color for header action buttons in the widget header.',
      },
    },
  },
  headerButtonRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description: 'Corner radius for header action buttons, in pixels. Range: 0-20.',
      },
    },
  },
  headerDividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Color and opacity of the horizontal divider line below the widget header.',
      },
    },
  },
  headerDividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 1,
      },
      ai: {
        description:
          'Width of the horizontal divider line below the widget header, in pixels. Range: 0-5.',
      },
    },
  },
  commentBoxPlaceholderMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
      ai: {
        description:
          'Font size for the closed comment box placeholder text on mobile devices, in pixels. Range: 12-16.',
      },
    },
  },
  commentBoxPlaceholderFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description:
          'Font style for the placeholder text in the closed comment input box (e.g. "Write a comment...").',
      },
    },
  },
  commentBoxPlaceholderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-3-color'),
      ai: {
        description: 'Text color for the placeholder text in the closed comment input box.',
      },
    },
  },
  closedCommentBoxFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
      ai: {
        description: 'Background fill color and opacity of the closed comment input box.',
      },
    },
  },
  closedCommentBoxBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Border color of the closed comment input box.',
      },
    },
  },
  closedCommentBoxBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 1,
      },
      ai: {
        description: 'Border width of the closed comment input box, in pixels. Range: 0-5.',
      },
    },
  },
  closedCommentBoxCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description: 'Corner radius of the closed comment input box, in pixels. Range: 0-40.',
      },
    },
  },
  openCommentBoxTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Text color for user input in the open (expanded) comment box.',
      },
    },
  },
  pluginsIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description:
          'Color of the formatting plugin icons (e.g. emoji, image) in the open comment box toolbar.',
      },
    },
  },
  openCommentBoxFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
      ai: {
        description: 'Background fill color and opacity of the open (expanded) comment box.',
      },
    },
  },
  openCommentBoxBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Border color of the open (expanded) comment box.',
      },
    },
  },
  buttonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
      ai: {
        description: 'Background color of the submit/post button in the open comment box.',
      },
    },
  },
  buttonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description: 'Font style for the submit/post button text in the open comment box.',
      },
    },
  },
  buttonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
      ai: {
        description: 'Text color of the submit/post button in the open comment box.',
      },
    },
  },
  buttonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description:
          'Corner radius of the submit/post button in the open comment box, in pixels. Range: 0-40.',
      },
    },
  },
  commentListBadgeColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
      ai: {
        description:
          'Color of the badge displayed on marked or highlighted comments (e.g. "Pinned", "Author").',
      },
    },
  },
  commentListHighlightColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
      ai: {
        description:
          'Background highlight color and opacity for marked or featured comments in the comment list.',
      },
    },
  },
  markedCommentCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description:
          'Corner radius of the background highlight on marked comments, in pixels. Range: 0-40.',
      },
    },
  },
  commentListCommentFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description: 'Font style for the main comment body text in the comment list.',
      },
    },
  },
  commentListCommentMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
      ai: {
        description:
          'Font size for the main comment body text on mobile devices, in pixels. Range: 12-20.',
      },
    },
  },
  commentListCommentTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Text color for the main comment body text in the comment list.',
      },
    },
  },
  commentListSecondaryFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description:
          'Font style for secondary text in the comment list (e.g. timestamps, edited labels).',
      },
    },
  },
  commentListSecondaryMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 12,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 12,
      },
      ai: {
        description:
          'Font size for secondary text in the comment list on mobile devices, in pixels. Range: 12-16.',
      },
    },
  },
  commentListSecondaryTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-3-color'),
      ai: {
        description:
          'Text color for secondary text in the comment list (e.g. timestamps, edited labels).',
      },
    },
  },
  commentListUsernameFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description: 'Font style for commenter usernames in the comment list.',
      },
    },
  },
  commentListUsernameMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 12,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 12,
      },
      ai: {
        description:
          'Font size for commenter usernames on mobile devices, in pixels. Range: 12-16.',
      },
    },
  },
  commentListUsernameTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Text color for commenter usernames in the comment list.',
      },
    },
  },
  actionsFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
      ai: {
        description: 'Font style for comment action links (e.g. "Reply", "Like", "Delete").',
      },
    },
  },
  actionsTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Text color for comment action links in the comment list.',
      },
    },
  },
  actionsIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Color of icons in comment action controls (e.g. like, reply icons).',
      },
    },
  },
  commentListDividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Color and opacity of the divider lines between comments in the comment list.',
      },
    },
  },
  widgetBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
      ai: {
        description: 'Background fill color and opacity of the entire comments widget container.',
      },
    },
  },
  widgetBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Border color and opacity of the comments widget container.',
      },
    },
  },
  widgetBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description: 'Border width of the comments widget container, in pixels. Range: 0-5.',
      },
    },
  },
  widgetCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description: 'Corner radius of the comments widget container, in pixels. Range: 0-20.',
      },
    },
  },
  commentsSpacing: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 16,
      },
      ai: {
        description:
          'Vertical spacing between comments in the comment list, in pixels. Range: 4-40 on desktop, 12-24 on mobile.',
      },
    },
  },
  sidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) => {
      return getStyleParamValue(isBlogComments) ? 0 : 20;
    },
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
      ai: {
        description:
          'Horizontal padding on the left and right sides of the comments widget content, in pixels. Range: 0-100.',
      },
    },
  },
  replyLeftPadding: {
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 20 : 48),
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 48,
      },
      mobileDefaultValue: {
        type: 'static',
        value: 20,
      },
      ai: {
        description:
          'Left indentation for nested reply comments, in pixels. Range: 0-48 on desktop, 20-30 on mobile.',
      },
    },
  },
  filledRatingStar: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
      ai: {
        description: 'Color of filled (active) stars in the ratings display.',
      },
    },
  },
  emptyRatingStar: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-system-disabled-color'),
      ai: {
        description: 'Color of empty (inactive) stars in the ratings display.',
      },
    },
  },
  ratingInfoFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-3-font'),
      ai: {
        description: 'Font style for the rating score text displayed alongside the star rating.',
      },
    },
  },
  ratingInfoColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
      ai: {
        description: 'Text color for the rating score text displayed alongside the star rating.',
      },
    },
  },
});
